"use client"

import React, { useEffect, useRef } from 'react'
import { cn } from '../../lib/utils'
import { UIMessage, UIMessageProps } from './ui-message'
import { UIScrollArea } from '../layout/ui-scroll-area'
import { UIButton } from '../buttons/ui-button'
import { UITypography } from '../display/ui-typography'
import { UIBox } from '../layout/ui-box'
import { ArrowDown, RefreshCw } from 'lucide-react'

// 메시지 데이터 인터페이스
export interface UIConversationMessage extends Omit<UIMessageProps, 'children'> {
  id: string
  content: string
  role: 'user' | 'assistant' | 'system'
  timestamp?: Date
}

// 스트리밍 응답 인터페이스
export interface UIStreamingResponse {
  content: string
  isComplete: boolean
  messageId?: string
}

// 대화 인터페이스
export interface UIConversationProps {
  // 메시지 데이터
  messages: UIConversationMessage[]

  // 스트리밍 응답
  streamingResponse?: UIStreamingResponse

  // 상태
  isLoading?: boolean
  isEmpty?: boolean

  // 실시간 기능
  onMessageSend?: (content: string) => Promise<void>
  onMessageEdit?: (id: string, content: string) => void
  onMessageDelete?: (id: string) => void
  onMessageRetry?: (id: string) => void

  // 스크롤 및 UI 옵션
  maxHeight?: string
  autoScroll?: boolean
  showScrollUIButton?: boolean

  // 메시지 표시 옵션
  showTimestamps?: boolean
  showAvatars?: boolean
  showRoles?: boolean
  messageVariant?: 'default' | 'detailed'

  // 로딩 및 빈 상태
  loadingComponent?: React.ReactNode
  emptyComponent?: React.ReactNode

  // 스타일링
  className?: string
  messagesClassName?: string

  // 접근성 및 i18n
  ariaLabel?: string
  locale?: 'ko' | 'en'

  // 고급 옵션
  enableVirtualization?: boolean
  messageActions?: (message: UIConversationMessage) => UIMessageProps['actions']
  onScrollToTop?: () => void
}

/**
 * UIConversation - AI 대화 스레드를 관리하는 컨테이너 컴포넌트
 * shadcn.io/ai Conversation 컴포넌트 기반으로 구현
 */
export const UIConversation = React.forwardRef<HTMLDivElement, UIConversationProps>(
  ({
    messages,
    streamingResponse,
    isLoading = false,
    isEmpty = false,
    onMessageSend,
    onMessageEdit,
    onMessageDelete,
    onMessageRetry,
    maxHeight = '600px',
    autoScroll = true,
    showScrollUIButton = true,
    showTimestamps = true,
    showAvatars = true,
    showRoles = false,
    messageVariant = 'default',
    loadingComponent,
    emptyComponent,
    className,
    messagesClassName,
    ariaLabel,
    locale = 'ko',
    enableVirtualization = false,
    messageActions,
    onScrollToTop,
    ...props
  }, ref) => {
    const scrollAreaRef = useRef<HTMLDivElement>(null)
    const messagesEndRef = useRef<HTMLDivElement>(null)
    const [showScrollToBottom, setShowScrollToBottom] = React.useState(false)
    const [isUserScrolling, setIsUserScrolling] = React.useState(false)

    // 자동 스크롤 로직
    const scrollToBottom = (smooth = true) => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({
          behavior: smooth ? 'smooth' : 'auto',
          block: 'end'
        })
      }
    }

    // 메시지가 추가되거나 스트리밍 중일 때 자동 스크롤
    useEffect(() => {
      if (autoScroll && !isUserScrolling) {
        scrollToBottom()
      }
    }, [messages.length, streamingResponse?.content, autoScroll, isUserScrolling])

    // 스크롤 이벤트 핸들링
    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
      const element = event.currentTarget
      const { scrollTop, scrollHeight, clientHeight } = element

      // 하단에서 50px 이내에 있으면 스크롤 버튼 숨김
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 50
      setShowScrollToBottom(!isNearBottom && showScrollUIButton)

      // 사용자가 스크롤 중인지 감지
      if (!isNearBottom) {
        setIsUserScrolling(true)
        // 3초 후 자동 스크롤 재개
        setTimeout(() => setIsUserScrolling(false), 3000)
      } else {
        setIsUserScrolling(false)
      }

      // 상단 스크롤 감지 (무한 스크롤용)
      if (scrollTop === 0 && onScrollToTop) {
        onScrollToTop()
      }
    }

    // 빈 상태 표시
    if (isEmpty || messages.length === 0) {
      return (
        <UIBox
          ref={ref}
          role="region"
          className={cn(
            "flex items-center justify-center h-96 rounded-xl",
            "bg-gradient-to-br from-white/[0.04] to-slate-50/[0.03] dark:from-white/[0.02] dark:to-slate-950/[0.015]",
            "border border-white/[0.08] dark:border-white/[0.06]",
            "shadow-xl shadow-black/[0.02] dark:shadow-black/[0.08]",
            className
          )}
          style={{ maxHeight }}
          aria-label={ariaLabel || (locale === 'ko' ? '대화 없음' : 'No conversation')}
          {...props}
        >
          {emptyComponent || (
            <UIBox className="text-center">
              <UIBox className="mb-2 text-4xl">💬</UIBox>
              <UITypography
                as="h3"
                variant="h5"
                weight="medium"
                className="mb-1 text-foreground/80 drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)] leading-relaxed"
              >
                {locale === 'ko' ? '새로운 대화를 시작하세요' : 'Start a new conversation'}
              </UITypography>
              <UITypography
                as="p"
                size="sm"
                className="text-foreground/70 drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)] leading-relaxed"
              >
                {locale === 'ko'
                  ? '메시지를 입력하여 AI와 대화를 시작할 수 있습니다.'
                  : 'Type a message to start chatting with AI.'
                }
              </UITypography>
            </UIBox>
          )}
        </UIBox>
      )
    }

    return (
      <UIBox
        ref={ref}
        role="region"
        className={cn(
          "relative flex flex-col rounded-xl transition-all duration-300 ease-out",
          "bg-gradient-to-br from-white/[0.04] to-slate-50/[0.025] dark:from-white/[0.02] dark:to-slate-950/[0.015]",
          "border border-white/[0.06] dark:border-white/[0.04]",
          "shadow-xl shadow-black/[0.02] dark:shadow-black/[0.08]",
          className
        )}
        style={{ maxHeight }}
        aria-label={ariaLabel || (locale === 'ko' ? 'AI 대화' : 'AI Conversation')}
        {...props}
      >
        {/* 메시지 영역 */}
        <UIScrollArea
          ref={scrollAreaRef}
          className={cn(
            "flex-1 pr-4",
            messagesClassName
          )}
          onScrollCapture={handleScroll}
        >
          <UIBox className="space-y-1">
            {/* 기존 메시지들 */}
            {messages.map((message) => (
              <UIMessage
                key={message.id}
                {...message}
                variant={messageVariant}
                showTimestamp={showTimestamps}
                showAvatar={showAvatars}
                showRole={showRoles}
                locale={locale}
                actions={messageActions ? messageActions(message) : message.actions}
              />
            ))}

            {/* 스트리밍 응답 */}
            {streamingResponse && (
              <UIMessage
                id={streamingResponse.messageId || 'streaming'}
                content={streamingResponse.content}
                role="assistant"
                variant={messageVariant}
                showTimestamp={false}
                showAvatar={showAvatars}
                showRole={showRoles}
                isStreaming={!streamingResponse.isComplete}
                locale={locale}
              />
            )}

            {/* 로딩 인디케이터 */}
            {isLoading && (
              <UIBox className="flex justify-center py-4">
                {loadingComponent || (
                  <UIBox className="flex items-center space-x-2 text-foreground/70">
                    <RefreshCw className="w-4 h-4 animate-spin drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]" />
                    <UITypography
                      as="span"
                      size="sm"
                      className="drop-shadow-sm leading-relaxed"
                    >
                      {locale === 'ko' ? '응답을 생성하고 있습니다...' : 'Generating a response...'}
                    </UITypography>
                  </UIBox>
                )}
              </UIBox>
            )}

            {/* 스크롤 앵커 */}
            <UIBox ref={messagesEndRef} />
          </UIBox>
        </UIScrollArea>

        {/* 하단으로 스크롤 버튼 */}
        {showScrollToBottom && (
          <UIBox className="absolute bottom-4 right-6 z-10">
            <UIButton
              size="sm"
              variant="outline"
              className={cn(
                "h-8 w-8 rounded-full transition-all duration-300 ease-out",
                "bg-gradient-to-br from-white/[0.08] to-slate-50/[0.06] dark:from-white/[0.06] dark:to-slate-950/[0.04]",
                "border-white/[0.12] dark:border-white/[0.08]",
                "shadow-lg shadow-black/[0.04] dark:shadow-black/[0.12]",
                "hover:-translate-y-1 hover:scale-110 hover:shadow-xl hover:shadow-black/[0.08] dark:hover:shadow-black/[0.20]"
              )}
              onClick={() => {
                scrollToBottom()
                setShowScrollToBottom(false)
                setIsUserScrolling(false)
              }}
            >
              <ArrowDown className="w-4 h-4 drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]" />
              <UITypography
                as="span"
                variant="small"
                className="sr-only drop-shadow-sm leading-relaxed"
              >
                {locale === 'ko' ? '하단으로 스크롤' : 'Scroll to bottom'}
              </UITypography>
            </UIButton>
          </UIBox>
        )}
      </UIBox>
    )
  }
)

UIConversation.displayName = "UIConversation"